// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
/* eslint-disable jsx-a11y/anchor-is-valid */
import { InputControl, SelectControl } from "../../molecules";
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  FormControl,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm, Controller, useWatch } from "react-hook-form";
import { Button, Loading } from "../../atoms";
import {
  IMerchant,
  useGetBankList,
  useGetCurrencyCodeList,
  useGetMerchantList,
  useMerchantBankAdd,
  useMerchantBankUpdate,
} from "../../../hooks";
import React, { useEffect, useMemo } from "react";
import { useUserMerchantId } from "./Merchant.state";
import { useNavigate } from "react-router-dom";
import { useDeleteMerchantOneBank } from "../../../hooks/merchantBank/useDeleteMerchantOneBank";
import { useSetSnackBar } from "../../../store/Snackbar.state";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  thirdStepFormSchema,
  ThirdStepFormValuesType,
  thirdStepInitialValues,
} from "./_formTypes";

// Alt hesap formu bileşeni parent dışında tanımlanmalı, diğer türlü her renderda yeniden mount/unmount oluyor ve focus özelliği kayboluyor.
type AccountInputProps = {
  id: number;
  currencyCode: string;
  iban: string;
  bankCode: string;
  accountOwner: string;
  control: any;
  currencyCodeList: { label: string; value: string }[] | undefined;
  bankList: { label: string; value: string }[];
  setValue: any;
  onDelete: () => void;
  isDeleteDisabled: boolean;
  isDesktop: boolean;
  detectBankFromIban: (ibanValue: string) => { label: string; value: string } | undefined;
};

const AccountInput = ({
  id,
  currencyCode,
  iban,
  bankCode,
  accountOwner,
  control,
  currencyCodeList,
  bankList,
  setValue,
  onDelete,
  isDeleteDisabled,
  isDesktop,
  detectBankFromIban,
}: AccountInputProps) => {
  const ibanValue = useWatch({ control, name: iban });

  useEffect(() => {
    if (ibanValue && ibanValue.length >= 9) {
      const matchedBank = detectBankFromIban(ibanValue);
      if (matchedBank) {
        setValue(bankCode, matchedBank.value);
      }
    }
  }, [ibanValue, bankCode, setValue]);

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" fontWeight="bold">Hesap {id}</Typography>
        <Button
          onClick={onDelete}
          variant="outlined"
          color="error"
          text="Sil"
          startIcon={<DeleteIcon />}
          disabled={isDeleteDisabled}
        />
      </Stack>
      <Stack
        width={"100%"}
        spacing={3}
        direction={isDesktop ? "row" : "column"}
      >
        <FormControl fullWidth>
          {currencyCodeList && (
            <Controller
              name={currencyCode}
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => {
                const selectedCurrency = currencyCodeList?.find(
                  (option) => option.value === value
                );

                return (
                  <Autocomplete
                    id={currencyCode}
                    options={currencyCodeList}
                    getOptionSelected={(option, value) =>
                      option.value === value
                    }
                    getOptionLabel={(option) => option.label}
                    value={selectedCurrency || null}
                    onChange={(_, newValue) => {
                      onChange(newValue ? newValue.value : "");
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Para Birimi" />
                    )}
                  />
                );
              }}
            />
          )}
        </FormControl>

        <FormControl fullWidth>
          <InputControl id={iban} name={iban} control={control} label="IBAN" />
        </FormControl>
      </Stack>
      <Stack
        width={"100%"}
        spacing={3}
        direction={isDesktop ? "row" : "column"}
      >
        <FormControl fullWidth>
          {bankList && (
            <Controller
              name={bankCode}
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => {
                const selectedBank = bankList.find(
                  (option) => option.value === value
                );

                return (
                  <Autocomplete
                    id={bankCode}
                    options={bankList}
                    getOptionSelected={(option, value) =>
                      option.value === value
                    }
                    getOptionLabel={(option) => option.label}
                    value={selectedBank || null}
                    onChange={(_, newValue) => {
                      onChange(newValue ? newValue.value : "");
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Banka Adı" />
                    )}
                  />
                );
              }}
            />
          )}
        </FormControl>

        <FormControl fullWidth>
          <InputControl
            id={accountOwner}
            name={accountOwner}
            control={control}
            label="Hesap Sahibi"
          />
        </FormControl>
      </Stack>
    </Stack>
  );
};

type MerchantAddFormCompanyStepProps = {
  onNext: () => void;
  onBack: () => void;
  merchant?: IMerchant;
  allData?: any;
  setAllData?: any;
};

export const MerchantAddFormBankStep = ({
  merchant,
  onNext,
  onBack,
  allData,
  setAllData,
}: MerchantAddFormCompanyStepProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { data: rawCurrencyCodeList } = useGetCurrencyCodeList({});
  const navigate = useNavigate();
  const setSnackbar = useSetSnackBar();
  const [merchantId] = useUserMerchantId();
  const { data: rawBankList } = useGetBankList({});
  const { mutate: deleteMerchantOneBank, isLoading: isDeleteOneLoading } = useDeleteMerchantOneBank();
  const { mutate: merchantBankAdd, isLoading } = useMerchantBankAdd();
  const { mutate: merchantBankUpdate, isUpdateLoading } =
    useMerchantBankUpdate();
  const { mutate: getMerchantList } = useGetMerchantList();
  const { control, reset, handleSubmit, setValue, getValues } = useForm<ThirdStepFormValuesType>({
    resolver: zodResolver(thirdStepFormSchema),
    defaultValues: thirdStepInitialValues,
  });

  const areAllAccountsEmpty = React.useCallback(() => {
    const formValues = getValues();
    let merchantDataRaw = localStorage.getItem("merchantData");
    let merchantData = {};
    if (merchantDataRaw) {
      try { merchantData = JSON.parse(merchantDataRaw); } catch {}
    }

    // currencyCode için özel kontrol - 0, "0", null, undefined, "" değerleri boş kabul et
    const isCurrencyCodeEmpty = (val: any) => {
      return val === null || val === undefined || val === "" || val === 0 || val === "0";
    };

    // String alanlar için kontrol - null, undefined, "" veya sadece boşluk
    const isStringEmpty = (val: any) => {
      return val === null || val === undefined || val === "" || (typeof val === "string" && val.trim() === "");
    };

    const currencyCodes = ["currencyCode", "currencyCode1", "currencyCode2"];
    const stringFields = [
      "iban", "iban1", "iban2",
      "bankCode", "bankCode1", "bankCode2",
      "accountOwner", "accountOwner1", "accountOwner2"
    ];

    // Tüm currencyCode alanlarını kontrol et
    const allCurrencyCodesEmpty = currencyCodes.every(field => {
      const formVal = formValues[field];
      const localVal = merchantData[field];
      return isCurrencyCodeEmpty(formVal) && isCurrencyCodeEmpty(localVal);
    });

    // Tüm string alanları kontrol et
    const allStringFieldsEmpty = stringFields.every(field => {
      const formVal = formValues[field];
      const localVal = merchantData[field];
      return isStringEmpty(formVal) && isStringEmpty(localVal);
    });

    return allCurrencyCodesEmpty && allStringFieldsEmpty;
  }, [getValues]);

  const currencyCodeList = useMemo(() => {
    return rawCurrencyCodeList?.data?.map(
      (currencyCode: { value: string; key: string }) => {
        return {
          label: `${currencyCode.value}`,
          value: currencyCode.key,
        };
      }
    );
  }, [rawCurrencyCodeList?.data]);

  function getCurrencyFlags(request) {
    const currencyCodes = [
      request.currencyCode,
      request.currencyCode1,
      request.currencyCode2,
    ];

    return {
      try: currencyCodes.includes(949),
      eur: currencyCodes.includes(978),
      usd: currencyCodes.includes(840),
    };
  }

  const bankList = useMemo(() => {
    return rawBankList?.data?.map((bank: { name: string; code: string }) => {
      return {
        label: `${bank.name}`,
        value: bank.code,
      };
    });
  }, [rawBankList?.data]);

  function getCurrencyFlags(request) {
    const currencyCodes = [
      request.currencyCode,
      request.currencyCode1,
      request.currencyCode2,
    ];

    return {
      try: currencyCodes.includes(949),
      eur: currencyCodes.includes(978),
      usd: currencyCodes.includes(840),
    };
  }

  const onSubmit = ({
    currencyCode,
    iban,
    bankCode,
    accountOwner,
    iban1,
    bankCode1,
    currencyCode1,
    accountOwner1,
    currencyCode2,
    iban2,
    bankCode2,
    accountOwner2,
  }: ThirdStepFormValuesType) => {
    const isAccount1Filled = !!(iban && iban.trim() !== "");
    const isAccount2Filled = !!(iban1 && iban1.trim() !== "");
    const isAccount3Filled = !!(iban2 && iban2.trim() !== "");

    const merchantIdValue = merchant?.merchantId ?? allData?.merchantId;

    const updateRequest = {
      merchantId: merchantIdValue,
      currencyCode: currencyCode && currencyCode !== "0" ? Number(currencyCode) : null,
      iban: iban && iban.trim() !== "" ? iban : null,
      bankCode: bankCode && bankCode.trim() !== "" ? bankCode : null,
      accountOwner: accountOwner && accountOwner.trim() !== "" ? accountOwner : null,
      currencyCode1: currencyCode1 && currencyCode1 !== "0" ? Number(currencyCode1) : null,
      iban1: iban1 && iban1.trim() !== "" ? iban1 : null,
      bankCode1: bankCode1 && bankCode1.trim() !== "" ? bankCode1 : null,
      accountOwner1: accountOwner1 && accountOwner1.trim() !== "" ? accountOwner1 : null,
      currencyCode2: currencyCode2 && currencyCode2 !== "0" ? Number(currencyCode2) : null,
      iban2: iban2 && iban2.trim() !== "" ? iban2 : null,
      bankCode2: bankCode2 && bankCode2.trim() !== "" ? bankCode2 : null,
      accountOwner2: accountOwner2 && accountOwner2.trim() !== "" ? accountOwner2 : null,
      bankType: isAccount1Filled ? Number(1) : null,
      bankType1: isAccount2Filled ? Number(1) : null,
      bankType2: isAccount3Filled ? Number(1) : null,
    };

    const addRequest = {
      merchantId: merchantIdValue,
      currencyCode: isAccount1Filled ? Number(currencyCode) : null,
      iban: isAccount1Filled ? iban : null,
      bankCode: isAccount1Filled ? bankCode : null,
      accountOwner: isAccount1Filled ? accountOwner : null,
      bankType: isAccount1Filled ? Number(1) : null,
      currencyCode1: isAccount2Filled ? Number(currencyCode1) : null,
      iban1: isAccount2Filled ? iban1 : null,
      bankCode1: isAccount2Filled ? bankCode1 : null,
      accountOwner1: isAccount2Filled ? accountOwner1 : null,
      bankType1: isAccount2Filled ? Number(1) : null,
      currencyCode2: isAccount3Filled ? Number(currencyCode2) : null,
      iban2: isAccount3Filled ? iban2 : null,
      bankCode2: isAccount3Filled ? bankCode2 : null,
      accountOwner2: isAccount3Filled ? accountOwner2 : null,
      bankType2: isAccount3Filled ? Number(1) : null,
    };

    setAllData({ ...allData, ...updateRequest });

    const updateLocalStorageFromRequest = (requestData: any, hasBankInfo?: boolean) => {
      try {
        const merchantDataRaw = localStorage.getItem("merchantData");
        if (!merchantDataRaw) return;
        const merchantData = JSON.parse(merchantDataRaw);

        merchantData["currencyCode"] = requestData.currencyCode ?? null;
        merchantData["iban"] = requestData.iban ?? null;
        merchantData["bankCode"] = requestData.bankCode ?? null;
        merchantData["accountOwner"] = requestData.accountOwner ?? null;

        merchantData["currencyCode1"] = requestData.currencyCode1 ?? null;
        merchantData["iban1"] = requestData.iban1 ?? null;
        merchantData["bankCode1"] = requestData.bankCode1 ?? null;
        merchantData["accountOwner1"] = requestData.accountOwner1 ?? null;

        merchantData["currencyCode2"] = requestData.currencyCode2 ?? null;
        merchantData["iban2"] = requestData.iban2 ?? null;
        merchantData["bankCode2"] = requestData.bankCode2 ?? null;
        merchantData["accountOwner2"] = requestData.accountOwner2 ?? null;

        // hasBankInfo parametresini de güncelle
        if (hasBankInfo !== undefined) {
          merchantData["hasBankInfo"] = hasBankInfo;
        }

        localStorage.setItem("merchantData", JSON.stringify(merchantData));
      } catch (e) {
        /* ignore */
      }
    };

    const hasExistingData = merchant && merchant?.hasBankInfo === true;

    if (hasExistingData) {
      merchantBankUpdate(
        { ...updateRequest, id: merchant?.id || 0 },
        {
          onSuccess(data) {
            if (data.isSuccess) {
              updateLocalStorageFromRequest(updateRequest, true);
              setAllData({ ...allData, ...getCurrencyFlags(updateRequest), ...updateRequest, hasBankInfo: true });
              
              if (merchant) {
                merchant.iban = updateRequest.iban || null;
                merchant.iban1 = updateRequest.iban1 || null;
                merchant.iban2 = updateRequest.iban2 || null;
                merchant.currencyCode = updateRequest.currencyCode || null;
                merchant.currencyCode1 = updateRequest.currencyCode1 || null;
                merchant.currencyCode2 = updateRequest.currencyCode2 || null;
                merchant.bankCode = updateRequest.bankCode || null;
                merchant.bankCode1 = updateRequest.bankCode1 || null;
                merchant.bankCode2 = updateRequest.bankCode2 || null;
                merchant.accountOwner = updateRequest.accountOwner || null;
                merchant.accountOwner1 = updateRequest.accountOwner1 || null;
                merchant.accountOwner2 = updateRequest.accountOwner2 || null;
                merchant.hasBankInfo = true;
              }
              
              setSnackbar({
                severity: "success",
                isOpen: true,
                description: data.message,
              });
            } else {
              setSnackbar({
                severity: "error",
                isOpen: true,
                description: data.message,
              });
            }
          },
          onError: () => {
            setSnackbar({
              severity: "error",
              isOpen: true,
              description: "İşlem sırasında bir hata oluştu",
            });
          },
        }
      );
    } else {
      merchantBankAdd(addRequest, {
        onSuccess(data) {
          if (data.isSuccess) {
            updateLocalStorageFromRequest(updateRequest, true);
            setAllData({ ...allData, ...getCurrencyFlags(updateRequest), ...updateRequest, hasBankInfo: true });
            
            if (merchant) {
              merchant.iban = updateRequest.iban || null;
              merchant.iban1 = updateRequest.iban1 || null;
              merchant.iban2 = updateRequest.iban2 || null;
              merchant.currencyCode = updateRequest.currencyCode || null;
              merchant.currencyCode1 = updateRequest.currencyCode1 || null;
              merchant.currencyCode2 = updateRequest.currencyCode2 || null;
              merchant.bankCode = updateRequest.bankCode || null;
              merchant.bankCode1 = updateRequest.bankCode1 || null;
              merchant.bankCode2 = updateRequest.bankCode2 || null;
              merchant.accountOwner = updateRequest.accountOwner || null;
              merchant.accountOwner1 = updateRequest.accountOwner1 || null;
              merchant.accountOwner2 = updateRequest.accountOwner2 || null;
              merchant.hasBankInfo = true;
            }
            
            // Add işlemi başarılı olduktan sonra Merchant/List'ten güncel bilgiyi çek
            getMerchantList(
              { 
                merchantId: merchantIdValue,
                page: 0,
                size: 1 
              },
              {
                onSuccess: (listData) => {
                  if (listData?.isSuccess && listData?.data?.length > 0) {
                    const updatedMerchant = listData.data[0];
                    // hasBankInfo'yu güncelle
                    if (merchant) {
                      merchant.hasBankInfo = updatedMerchant.hasBankInfo;
                    }
                  }
                },
                onError: (error) => {
                  console.error("Merchant bilgisi yeniden çekilemedi:", error);
                }
              }
            );
            
            setSnackbar({
              severity: "success",
              isOpen: true,
              description: data.message,
            });
          } else {
            setSnackbar({
              severity: "error",
              isOpen: true,
              description: data.message,
            });
          }
        },
        onError: () => {
          setSnackbar({
            severity: "error",
            isOpen: true,
            description: "İşlem sırasında bir hata oluştu",
          });
        },
      });
    }
  };

  // Boş hesapları tespit edip dolu hesapları yukarı kaydıran fonksiyon
  const shiftAccountsUp = (merchantData: any) => {
    const accounts = [
      {
        currencyCode: merchantData?.currencyCode,
        iban: merchantData?.iban,
        bankCode: merchantData?.bankCode,
        accountOwner: merchantData?.accountOwner,
        bankOrder: 0 
      },
      {
        currencyCode: merchantData?.currencyCode1,
        iban: merchantData?.iban1,
        bankCode: merchantData?.bankCode1,
        accountOwner: merchantData?.accountOwner1,
        bankOrder: 1 
      },
      {
        currencyCode: merchantData?.currencyCode2,
        iban: merchantData?.iban2,
        bankCode: merchantData?.bankCode2,
        accountOwner: merchantData?.accountOwner2,
        bankOrder: 2
      }
    ];

    const nonEmptyAccounts = accounts.filter(acc => {
      const isCurrencyCodeEmpty = acc.currencyCode === null || acc.currencyCode === undefined || acc.currencyCode === "" || acc.currencyCode === 0 || acc.currencyCode === "0";
      const isIbanEmpty = !acc.iban || acc.iban.toString().trim() === "";
      const isBankCodeEmpty = !acc.bankCode || acc.bankCode.toString().trim() === "";
      const isAccountOwnerEmpty = !acc.accountOwner || acc.accountOwner.toString().trim() === "";
      
      return !(isCurrencyCodeEmpty && isIbanEmpty && isBankCodeEmpty && isAccountOwnerEmpty);
    });

    while (nonEmptyAccounts.length < 3) {
      nonEmptyAccounts.push({
        currencyCode: "",
        iban: "",
        bankCode: "",
        accountOwner: "",
        bankOrder: -1
      });
    }

    return {
      currencyCode: nonEmptyAccounts[0].currencyCode,
      iban: nonEmptyAccounts[0].iban,
      bankCode: nonEmptyAccounts[0].bankCode,
      accountOwner: nonEmptyAccounts[0].accountOwner,
      currencyCode1: nonEmptyAccounts[1].currencyCode,
      iban1: nonEmptyAccounts[1].iban,
      bankCode1: nonEmptyAccounts[1].bankCode,
      accountOwner1: nonEmptyAccounts[1].accountOwner,
      currencyCode2: nonEmptyAccounts[2].currencyCode,
      iban2: nonEmptyAccounts[2].iban,
      bankCode2: nonEmptyAccounts[2].bankCode,
      accountOwner2: nonEmptyAccounts[2].accountOwner,
      bankOrders: [nonEmptyAccounts[0].bankOrder, nonEmptyAccounts[1].bankOrder, nonEmptyAccounts[2].bankOrder]
    };
  };

  useEffect(() => {
    if (!!merchant && JSON.stringify(merchant) !== "{}") {
      const merchantDataRaw = localStorage.getItem("merchantData");
      let storedBankOrders = null;
      if (merchantDataRaw) {
        try {
          const merchantData = JSON.parse(merchantDataRaw);
          storedBankOrders = merchantData.bankOrders;
        } catch {}
      }
      
      // Hesapları yukarı kaydır
      const shiftedData = shiftAccountsUp(merchant);
      
      const finalBankOrders = storedBankOrders || shiftedData.bankOrders;
      setBankOrders(finalBankOrders);
      
      reset({
        currencyCode: shiftedData.currencyCode?.toString() || "",
        iban: shiftedData.iban || "",
        bankCode: shiftedData.bankCode || "",
        accountOwner: shiftedData.accountOwner || "",
        iban1: shiftedData.iban1 || "",
        iban2: shiftedData.iban2 || "",
        currencyCode1: (shiftedData.currencyCode1 && shiftedData.currencyCode1 !== 0 && shiftedData.currencyCode1 !== "0") ? shiftedData.currencyCode1?.toString() : "",
        currencyCode2: (shiftedData.currencyCode2 && shiftedData.currencyCode2 !== 0 && shiftedData.currencyCode2 !== "0") ? shiftedData.currencyCode2?.toString() : "",
        bankCode1: shiftedData.bankCode1 || "",
        bankCode2: shiftedData.bankCode2 || "",
        accountOwner1: shiftedData.accountOwner1 || "",
        accountOwner2: shiftedData.accountOwner2 || "",
      });
      
      // Dolu hesap sayısını hesapla
      let filledAccounts = 0;
      if (shiftedData.iban && shiftedData.iban.trim() !== "") filledAccounts++;
      if (shiftedData.iban1 && shiftedData.iban1.trim() !== "") filledAccounts++;
      if (shiftedData.iban2 && shiftedData.iban2.trim() !== "") filledAccounts++;
      
      setAccountsCount(filledAccounts || 1);
    }
  }, [merchant, allData, reset]);

  // --- helper to detect bank from IBAN ---
  const detectBankFromIban = (ibanValue: string) => {
    if (!ibanValue) return null;
    return bankList?.find((b) => b.value == ibanValue.substring(5, 9));
  };

  const [accountsCount, setAccountsCount] = React.useState(1);
  const [deleteDialog, setDeleteDialog] = React.useState<{ open: boolean; accountId: number | null }>({ open: false, accountId: null });
  const [bankOrders, setBankOrders] = React.useState<number[]>([0, 1, 2]);

  const handleDeleteAccount = (accountId: number) => {
    setDeleteDialog({ open: true, accountId });
  };

  const confirmDeleteAccount = () => {
    const accountId = deleteDialog.accountId;

    // Merchant/List response'undaki merchantId öncelikli kullanılmalı
    const merchantIdForDelete = merchant?.merchantId ?? allData?.merchantId;

    if (!merchantIdForDelete || accountId === null || accountId === undefined) {
      setSnackbar({
        severity: "error",
        isOpen: true,
        description: "MerchantId veya hesap bulunamadı!",
      });
      setDeleteDialog({ open: false, accountId: null });
      return;
    }
    
    const realBankOrder = bankOrders[accountId];
    
    deleteMerchantOneBank({
      merchantId: merchantIdForDelete,
      bankOrder: realBankOrder, // Korunan gerçek bankOrder değeri
    }, {
      onSuccess: (data) => {
        if (data.isSuccess) {
          try {
            const merchantDataRaw = localStorage.getItem("merchantData");
            const formValues = getValues();

            // 0: ana hesap, 1: Hesap 2, 2: Hesap 3
            const emptySlot = { currencyCode: "", iban: "", bankCode: "", accountOwner: "" };

            const formSlots = [
              {
                currencyCode: formValues.currencyCode || null,
                iban: formValues.iban || null,
                bankCode: formValues.bankCode || null,
                accountOwner: formValues.accountOwner || null,
                bankOrder: bankOrders[0],
              },
              {
                currencyCode: formValues.currencyCode1 || null,
                iban: formValues.iban1 || null,
                bankCode: formValues.bankCode1 || null,
                accountOwner: formValues.accountOwner1 || null,
                bankOrder: bankOrders[1],
              },
              {
                currencyCode: formValues.currencyCode2 || null,
                iban: formValues.iban2 || null,
                bankCode: formValues.bankCode2 || null,
                accountOwner: formValues.accountOwner2 || null,
                bankOrder: bankOrders[2],
              },
            ];

            const newFormSlots = [...formSlots];
            newFormSlots.splice(accountId, 1);
            newFormSlots.push({ ...emptySlot, bankOrder: -1 });

            const newBankOrders = newFormSlots.map(slot => slot.bankOrder);
            setBankOrders(newBankOrders);

            setValue("currencyCode", newFormSlots[0].currencyCode);
            setValue("iban", newFormSlots[0].iban);
            setValue("bankCode", newFormSlots[0].bankCode);
            setValue("accountOwner", newFormSlots[0].accountOwner);

            setValue("currencyCode1", newFormSlots[1].currencyCode);
            setValue("iban1", newFormSlots[1].iban);
            setValue("bankCode1", newFormSlots[1].bankCode);
            setValue("accountOwner1", newFormSlots[1].accountOwner);

            setValue("currencyCode2", newFormSlots[2].currencyCode);
            setValue("iban2", newFormSlots[2].iban);
            setValue("bankCode2", newFormSlots[2].bankCode);
            setValue("accountOwner2", newFormSlots[2].accountOwner);

            const updatedAllData = {
              ...allData,
              currencyCode: newFormSlots[0].currencyCode && newFormSlots[0].currencyCode !== "0" ? Number(newFormSlots[0].currencyCode) : null,
              iban: newFormSlots[0].iban && newFormSlots[0].iban.trim() !== "" ? newFormSlots[0].iban : null,
              bankCode: newFormSlots[0].bankCode && newFormSlots[0].bankCode.trim() !== "" ? newFormSlots[0].bankCode : null,
              accountOwner: newFormSlots[0].accountOwner && newFormSlots[0].accountOwner.trim() !== "" ? newFormSlots[0].accountOwner : null,
              currencyCode1: newFormSlots[1].currencyCode && newFormSlots[1].currencyCode !== "0" ? Number(newFormSlots[1].currencyCode) : null,
              iban1: newFormSlots[1].iban && newFormSlots[1].iban.trim() !== "" ? newFormSlots[1].iban : null,
              bankCode1: newFormSlots[1].bankCode && newFormSlots[1].bankCode.trim() !== "" ? newFormSlots[1].bankCode : null,
              accountOwner1: newFormSlots[1].accountOwner && newFormSlots[1].accountOwner.trim() !== "" ? newFormSlots[1].accountOwner : null,
              currencyCode2: newFormSlots[2].currencyCode && newFormSlots[2].currencyCode !== "0" ? Number(newFormSlots[2].currencyCode) : null,
              iban2: newFormSlots[2].iban && newFormSlots[2].iban.trim() !== "" ? newFormSlots[2].iban : null,
              bankCode2: newFormSlots[2].bankCode && newFormSlots[2].bankCode.trim() !== "" ? newFormSlots[2].bankCode : null,
              accountOwner2: newFormSlots[2].accountOwner && newFormSlots[2].accountOwner.trim() !== "" ? newFormSlots[2].accountOwner : null,
            };
            setAllData(updatedAllData);
            
            
            // localStorage tarafını da updatedAllData ile güncelle
            if (merchantDataRaw) {
              const merchantData = JSON.parse(merchantDataRaw);

              merchantData["currencyCode"] = updatedAllData.currencyCode ?? null;
              merchantData["iban"] = updatedAllData.iban ?? null;
              merchantData["bankCode"] = updatedAllData.bankCode ?? null;
              merchantData["accountOwner"] = updatedAllData.accountOwner ?? null;

              merchantData["currencyCode1"] = updatedAllData.currencyCode1 ?? null;
              merchantData["iban1"] = updatedAllData.iban1 ?? null;
              merchantData["bankCode1"] = updatedAllData.bankCode1 ?? null;
              merchantData["accountOwner1"] = updatedAllData.accountOwner1 ?? null;

              merchantData["currencyCode2"] = updatedAllData.currencyCode2 ?? null;
              merchantData["iban2"] = updatedAllData.iban2 ?? null;
              merchantData["bankCode2"] = updatedAllData.bankCode2 ?? null;
              merchantData["accountOwner2"] = updatedAllData.accountOwner2 ?? null;

              merchantData["bankOrders"] = newBankOrders;

              localStorage.setItem("merchantData", JSON.stringify(merchantData));
            }

            // Hesap sayısını hesapla
            const hasSecond =
              !!updatedAllData.iban1 ||
              !!updatedAllData.bankCode1 ||
              !!updatedAllData.currencyCode1 ||
              !!updatedAllData.accountOwner1;
            const hasThird =
              !!updatedAllData.iban2 ||
              !!updatedAllData.bankCode2 ||
              !!updatedAllData.currencyCode2 ||
              !!updatedAllData.accountOwner2;

            let nextCount = 1;
            if (hasSecond) nextCount = 2;
            if (hasThird) nextCount = 3;
            setAccountsCount(nextCount);
            
            setAllData(updatedAllData);

            const hasAnyRemainingAccount = [
              {
                currencyCode: updatedAllData.currencyCode,
                iban: updatedAllData.iban,
                bankCode: updatedAllData.bankCode,
                accountOwner: updatedAllData.accountOwner,
              },
              {
                currencyCode: updatedAllData.currencyCode1,
                iban: updatedAllData.iban1,
                bankCode: updatedAllData.bankCode1,
                accountOwner: updatedAllData.accountOwner1,
              },
              {
                currencyCode: updatedAllData.currencyCode2,
                iban: updatedAllData.iban2,
                bankCode: updatedAllData.bankCode2,
                accountOwner: updatedAllData.accountOwner2,
              },
            ].some((account) => {
              const hasCurrency = !!account.currencyCode && account.currencyCode !== 0 && account.currencyCode !== "0";
              const hasIban = !!account.iban && account.iban.toString().trim() !== "";
              const hasBankCode = !!account.bankCode && account.bankCode.toString().trim() !== "";
              const hasOwner = !!account.accountOwner && account.accountOwner.toString().trim() !== "";
              return hasCurrency || hasIban || hasBankCode || hasOwner;
            });

            if (merchant) {
              merchant.iban = updatedAllData.iban || null;
              merchant.iban1 = updatedAllData.iban1 || null;
              merchant.iban2 = updatedAllData.iban2 || null;
              merchant.currencyCode = updatedAllData.currencyCode || null;
              merchant.currencyCode1 = updatedAllData.currencyCode1 || null;
              merchant.currencyCode2 = updatedAllData.currencyCode2 || null;
              merchant.bankCode = updatedAllData.bankCode || null;
              merchant.bankCode1 = updatedAllData.bankCode1 || null;
              merchant.bankCode2 = updatedAllData.bankCode2 || null;
              merchant.accountOwner = updatedAllData.accountOwner || null;
              merchant.accountOwner1 = updatedAllData.accountOwner1 || null;
              merchant.accountOwner2 = updatedAllData.accountOwner2 || null;
            }
            
            setSnackbar({
              severity: "success",
              isOpen: true,
              description: "Hesap başarıyla silindi",
            });

          } catch (e) {
          }
        } else {
          setSnackbar({
            severity: "error",
            isOpen: true,
            description: data.message || "Hesap silinemedi!",
          });
        }
        setDeleteDialog({ open: false, accountId: null });
      },
      onError: (err) => {
        setSnackbar({
          severity: "error",
          isOpen: true,
          description: err?.message || "Hesap silinemedi!",
        });
        setDeleteDialog({ open: false, accountId: null });
      },
    });
  };

  const addAccount = () => {
    if (accountsCount < 3) {
      setAccountsCount(accountsCount + 1);
    } else {
      setSnackbar({
        severity: "error",
        isOpen: true,
        description: "Daha Fazla Hesap Ekleyemezsiniz",
      });
    }
  };

  const isAccountEmpty = (accountIdx = 0) => {
    // accountIdx: 0 = ana hesap, 1 = hesap2, 2 = hesap3
    const formValues = getValues();
    const fields = [
      ["currencyCode", "iban", "bankCode", "accountOwner"],
      ["currencyCode1", "iban1", "bankCode1", "accountOwner1"],
      ["currencyCode2", "iban2", "bankCode2", "accountOwner2"],
    ];
    return fields[accountIdx].every(
      (field) => !formValues[field] || formValues[field].toString().trim() === ""
    );
  };

  const renderAccounts = () => {
    const currencyCodes = ["currencyCode1", "currencyCode2"];
    const ibans = ["iban1", "iban2"];
    const bankCodes = ["bankCode1", "bankCode2"];
    const accountOwners = ["accountOwner1", "accountOwner2"];
    let accounts = [];
    for (let i = 0; i < accountsCount - 1; i++) {
      accounts.push(
        <AccountInput
          key={i}
          id={i + 2}
          currencyCode={currencyCodes[i]}
          iban={ibans[i]}
          bankCode={bankCodes[i]}
          accountOwner={accountOwners[i]}
          control={control}
          currencyCodeList={currencyCodeList}
          bankList={bankList ?? []}
          setValue={setValue}
          onDelete={() => handleDeleteAccount(i + 1)}
          isDeleteDisabled={isAccountEmpty(i + 1)}
          isDesktop={isDesktop}
          detectBankFromIban={detectBankFromIban}
        />
      );
    }
    return accounts;
  };

  // --- also watch main account IBAN ---
  const ibanMain = useWatch({ control, name: "iban" });
  useEffect(() => {
    console.log('ibanMain changed:', ibanMain);
    if (ibanMain && ibanMain.length >= 9) {
      const matchedBank = detectBankFromIban(ibanMain);
      if (matchedBank) {
        setValue("bankCode", matchedBank.value);
      }
    }
  }, [ibanMain, setValue]);

  // Ana hesap inputlarını izleyelim:
  const mainAccountValues = useWatch({
    control,
    name: ["currencyCode", "iban", "bankCode", "accountOwner"],
  });

  const isMainAccountEmpty = () => {
    const formValues = getValues();
    const currencyCode = formValues.currencyCode;
    const iban = formValues.iban;
    const bankCode = formValues.bankCode;
    const accountOwner = formValues.accountOwner;
    
    const isCurrencyCodeEmpty = currencyCode === null || currencyCode === undefined || currencyCode === "" || currencyCode === 0 || currencyCode === "0";
    
    const isIbanEmpty = !iban || iban.toString().trim() === "";
    const isBankCodeEmpty = !bankCode || bankCode.toString().trim() === "";
    const isAccountOwnerEmpty = !accountOwner || accountOwner.toString().trim() === "";
    
    return isCurrencyCodeEmpty && isIbanEmpty && isBankCodeEmpty && isAccountOwnerEmpty;
  };

  return (
    <>
      {(isLoading || isDeleteOneLoading || isUpdateLoading) && <Loading />}
      <Box p={isDesktop ? 3 : 2}>
        <Stack spacing={3}>
          <Card variant="outlined">
            <CardHeader
              title="Banka Hesap Bilgileri"
              action={
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    text="+ Hesap Ekle"
                    onClick={addAccount}
                    disabled={accountsCount >= 3}
                  />
                </Stack>
              }
            />
            <CardContent>
              <Stack spacing={4} divider={<Divider sx={{ my: 2 }} />}> 
                <Stack spacing={3}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight="bold">
                      Ana Hesap
                    </Typography>
                    {!isMainAccountEmpty() && (
                      <Button
                        onClick={() => handleDeleteAccount(0)}
                        variant="outlined"
                        color="error"
                        text="Sil"
                        startIcon={<DeleteIcon />}
                      />
                    )}
                  </Stack>
                  <Stack
                    width={"100%"}
                    spacing={3}
                    direction={isDesktop ? "row" : "column"}
                  >
                    <FormControl fullWidth>
                      {currencyCodeList && (
                        <Controller
                          name="currencyCode"
                          control={control}
                          defaultValue=""
                          render={({ field: { onChange, value } }) => {
                            const selectedCurrency = currencyCodeList?.find(
                              (option) => option.value === value
                            );
                            return (
                              <Autocomplete
                                id="currencyCode"
                                options={currencyCodeList}
                                getOptionSelected={(option, value) =>
                                  option.value === value
                                }
                                getOptionLabel={(option) => option.label}
                                value={selectedCurrency || null}
                                onChange={(_, newValue) => {
                                  onChange(newValue ? newValue.value : "");
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} label="Para Birimi" />
                                )}
                              />
                            );
                          }}
                        />
                      )}
                    </FormControl>
                    <FormControl fullWidth>
                      <InputControl id="iban" name="iban" control={control} label="IBAN" />
                    </FormControl>
                  </Stack>
                  <Stack
                    width={"100%"}
                    spacing={3}
                    direction={isDesktop ? "row" : "column"}
                  >
                    <FormControl fullWidth>
                      {bankList && (
                        <Controller
                          name="bankCode"
                          control={control}
                          defaultValue=""
                          render={({ field: { onChange, value } }) => {
                            const selectedBank = bankList.find(
                              (option) => option.value === value
                            );
                            return (
                              <Autocomplete
                                id="bankCode"
                                options={bankList}
                                getOptionSelected={(option, value) =>
                                  option.value === value
                                }
                                getOptionLabel={(option) => option.label}
                                value={selectedBank || null}
                                onChange={(_, newValue) => {
                                  onChange(newValue ? newValue.value : "");
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} label="Banka Adı" />
                                )}
                              />
                            );
                          }}
                        />
                      )}
                    </FormControl>
                    <FormControl fullWidth>
                      <InputControl
                        id="accountOwner"
                        name="accountOwner"
                        control={control}
                        label="Hesap Sahibi"
                      />
                    </FormControl>
                  </Stack>
                </Stack>
                {renderAccounts()}
              </Stack>
            </CardContent>
          </Card>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              text="Kaydet"
              onClick={handleSubmit(onSubmit)}
            />
          </Stack>
        </Stack>
      </Box>
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, accountId: null })}
      >
        <DialogTitle>Hesabı Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bu hesabı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            text="İptal"
            onClick={() => setDeleteDialog({ open: false, accountId: null })}
            variant="outlined"
          />
          <Button
            text="Sil"
            onClick={confirmDeleteAccount}
            variant="contained"
            color="error"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};